import { useState } from "react";
import { apiFetch } from "../api";
import { theme } from "../theme";
import { useNavigate } from "react-router-dom";
import { useApp } from "../AppContext";
import ShopSetupNotice from "../components/ShopSetupNotice";

const castka = (n) => new Intl.NumberFormat("cs-CZ").format(n);

// Základní frekvence je v ceně a není to příplatek, takže ji backend
// neposílá — zbytek se doplní z /plans.
const FREQ_ZAKLADNI = { value: null, label: "12h (základní)", price: "v ceně" };

// ── Nastavení jako plná stránka (dřív SettingsModal) ───────────────────────────
export default function SettingsPage() {
  const { token, license, saveLicense, restartTour, restartOnboarding, shopMissing, cenik, frekvence, nazvyPlanu } = useApp();

  const [shopId] = useState(license?.shop_id || "");
  const [notifyEmail, setNotifyEmail] = useState(license?.email || "");
  const [notifyThreshold, setNotifyThreshold] = useState(license?.notify_threshold || 10);
  const [frequencyAddon, setFrequencyAddon] = useState(license?.frequency_addon || null);
  const [changePlan, setChangePlan] = useState(null);
  const [changeBilling, setChangeBilling] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const freqOptions = [
    FREQ_ZAKLADNI,
    ...frekvence.map(a => ({ value: a.key, label: a.key, price: `+${a.price_monthly} Kč/měs` })),
  ];


  const handleSave = async () => {
    setLoading(true); setError("");
    try {
      const currentAddon = license?.frequency_addon || null;
      const newAddon = frequencyAddon;

      // Přepnutí zpět na 12h základní — zrušit addon subscription
      if (currentAddon && !newAddon) {
        await apiFetch("/cancel-addon", token, { method: "POST" });
      }

      // Výběr nového placeného addonu — spustit Stripe checkout
      if (newAddon && newAddon !== currentAddon) {
        const res = await apiFetch("/create-addon-checkout", token, { method: "POST", body: JSON.stringify({ addon: newAddon }) });
        if (res.url) { window.location.href = res.url; return; }
      }

      // Uložit ostatní nastavení
      const updated = await apiFetch("/me/settings", token, { method: "PATCH", body: JSON.stringify({ shop_id: shopId || null, notify_email: notifyEmail || null, notify_threshold: notifyThreshold, frequency_addon: newAddon || null }) });
      saveLicense(updated); setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch(e) { setError(e.message || e.detail || JSON.stringify(e)); }
    setLoading(false);
  };

  const card = {
    background: theme.bg, border: `1.5px solid ${theme.line}`, borderRadius: theme.rCard,
    padding: 24, marginBottom: 20, boxShadow: theme.shadowSm,
  };
  const lbl = { display: "block", fontSize: 12, fontWeight: 700, color: theme.inkSoft, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 };
  const inp = { width: "100%", border: `1.5px solid ${theme.line}`, borderRadius: 10, padding: "11px 14px", fontSize: 14, color: theme.ink, background: theme.bgSoft, fontFamily: "inherit", boxSizing: "border-box" };
  const h2 = { fontSize: 16, fontWeight: 800, color: theme.ink, marginBottom: 16, letterSpacing: "-0.2px" };

  return (
    <>
      <div className="fadeUp" style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.6px", color: theme.ink, marginBottom: 6 }}>Nastavení</h1>
        <p style={{ color: theme.inkSoft, fontSize: 14 }}>Plán, frekvence kontrol a notifikace</p>
      </div>

      {/* Info o plánu */}
      <div style={{ ...card, background: theme.blueSoft, border: `1.5px solid ${theme.blueLine}`, display: "flex", gap: 24, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 130 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: theme.blueDark, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Plán</p>
          <p style={{ fontSize: 18, fontWeight: 800, color: theme.ink }}>{nazvyPlanu[license?.plan] || license?.plan || "—"}</p>
        </div>
        <div style={{ flex: 1, minWidth: 130 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: theme.blueDark, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Max produktů</p>
          <p style={{ fontSize: 18, fontWeight: 800, color: theme.ink, fontVariantNumeric: theme.tnum }}>{license?.max_products || 20}</p>
        </div>
        <div style={{ flex: 1, minWidth: 130 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: theme.blueDark, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Platnost do</p>
          <p style={{ fontSize: 18, fontWeight: 800, color: theme.ink, fontVariantNumeric: theme.tnum }}>{license?.valid_until ? new Date(license.valid_until).toLocaleDateString("cs-CZ") : "—"}</p>
        </div>
      </div>

      {/* Změnit plán */}
      <div style={card}>
        <h2 style={h2}>Změnit plán</h2>
        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          {cenik.map(plan => {
            const vybrany = changePlan === plan.key;
            const rocne = changeBilling === "yearly";
            return (
              <button key={plan.key} onClick={() => setChangePlan(plan.key)} style={{ flex: 1, minWidth: 104, padding: "11px 8px", borderRadius: theme.rBtn, border: "1.5px solid " + (vybrany ? theme.blue : theme.line), background: vybrany ? theme.blueSoft : theme.bg, color: vybrany ? theme.blue : theme.inkSoft, cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, lineHeight: 1.25 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{plan.name}</span>
                <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: theme.tnum, color: vybrany ? theme.blue : theme.ink }}>
                  {castka(rocne ? plan.price_yearly : plan.price_monthly)} Kč
                </span>
                <span style={{ fontSize: 11, color: theme.mut, fontWeight: 500 }}>
                  <span style={{ fontVariantNumeric: theme.tnum }}>{plan.max_products}</span> produktů
                </span>
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {(() => {
            const zvoleny = cenik.find(c => c.key === changePlan);
            const obdobi = (klic, popis, cena, doplnek) => (
              <button onClick={() => setChangeBilling(klic)} style={{ flex: 1, padding: "9px 8px", borderRadius: theme.rBtn, border: "1.5px solid " + (changeBilling === klic ? theme.blue : theme.line), background: changeBilling === klic ? theme.blueSoft : theme.bg, color: changeBilling === klic ? theme.blue : theme.inkSoft, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", lineHeight: 1.35 }}>
                {popis}{cena != null && <> · <span style={{ fontWeight: 700, fontVariantNumeric: theme.tnum }}>{castka(cena)} Kč</span></>}
                {doplnek && <><br /><span style={{ fontSize: 10, color: theme.ok, fontWeight: 700 }}>{doplnek}</span></>}
              </button>
            );
            return (
              <>
                {obdobi("monthly", "Měsíčně", zvoleny?.price_monthly ?? null, null)}
                {obdobi("yearly", "Ročně", zvoleny?.price_yearly ?? null,
                  zvoleny ? `ušetříte ${castka(zvoleny.usetrite_rocne)} Kč` : "-17 %")}
              </>
            );
          })()}
        </div>
        {changePlan && (
          <button onClick={async () => {
            try {
              const res = await apiFetch("/change-plan", token, { method: "POST", body: JSON.stringify({ plan: changePlan, email: license.email, billing: changeBilling }) });
              if (res.url) window.location.href = res.url;
            } catch(e) { setError(e.message); }
          }} style={{ width: "100%", background: theme.blue, border: "none", borderRadius: theme.rBtn, padding: "12px", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {(() => {
              const p = cenik.find(c => c.key === changePlan);
              const cena = p ? castka(changeBilling === "yearly" ? p.price_yearly : p.price_monthly) : null;
              const obdobi = changeBilling === "yearly" ? "ročně" : "měsíčně";
              return <>Přejít na {p?.name || changePlan}{cena && <> · <span style={{ fontVariantNumeric: theme.tnum }}>{cena} Kč</span> {obdobi}</>} →</>;
            })()}
          </button>
        )}
      </div>

      {/* Frekvence + notifikace */}
      <div style={card}>
        <h2 style={h2}>Kontroly a notifikace</h2>
        <label style={lbl}>Frekvence kontrol</label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, marginBottom: 20 }}>
          {freqOptions.map(opt => (
            <div key={String(opt.value)} onClick={() => setFrequencyAddon(opt.value)} style={{ border: "1.5px solid " + (frequencyAddon === opt.value ? theme.blue : theme.line), borderRadius: theme.rCard, padding: "10px 14px", cursor: "pointer", background: frequencyAddon === opt.value ? theme.blueSoft : theme.bg }}>
              <p style={{ fontWeight: 700, fontSize: 14, color: frequencyAddon === opt.value ? theme.blue : theme.ink, fontVariantNumeric: theme.tnum }}>{opt.label}</p>
              <p style={{ fontSize: 12, color: theme.inkSoft, marginTop: 2 }}>{opt.price}</p>
            </div>
          ))}
        </div>
        <label style={lbl}>Výchozí hranice pro všechny produkty</label>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 14, color: theme.mut }}>#</span>
          <input
            type="number" min="1" max="50"
            value={notifyThreshold}
            onChange={e => setNotifyThreshold(e.target.value === "" ? "" : parseInt(e.target.value))}
            style={{ ...inp, width: 100, marginBottom: 0, fontVariantNumeric: theme.tnum }}
          />
        </div>
        <p style={{ margin: "0 0 4px", fontSize: 12.5, color: theme.inkSoft, lineHeight: 1.5 }}>
          Uvnitř téhle hranice vám dáme vědět o každém poklesu i o vypadnutí z ní.
        </p>
        <p style={{ margin: "0 0 20px", fontSize: 12.5, color: theme.mut, lineHeight: 1.5 }}>
          Jednotlivý produkt může mít vlastní hranici, nastavíte ji v jeho detailu.
        </p>

        <label style={lbl}>Email pro notifikace</label>
        <input value={notifyEmail} onChange={e => setNotifyEmail(e.target.value)} placeholder="email@example.com" style={inp} />
      </div>

      {/* Průvodce */}
      <div style={card}>
        <h2 style={h2}>Průvodce</h2>
        {shopMissing && (
          <div style={{ marginBottom: 12 }}>
            {/* Jsme na /nastaveni, takže odkaz sem by mířil sám na sebe —
                akce rovnou znovu otevře průvodce nastavením e-shopu. */}
            <ShopSetupNotice compact title="E-shop zatím nastavený není"
              actionLabel="Spustit nastavení" onAction={restartOnboarding}>
              Bez něj Rankio nepozná vaši nabídku na Heurece a pozice se neměří.
            </ShopSetupNotice>
          </div>
        )}
        <button onClick={() => { restartTour(); navigate("/"); }} style={{ width: "100%", background: theme.bg, border: `1.5px solid ${theme.line}`, borderRadius: theme.rBtn, padding: "11px", color: theme.inkSoft, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
          Projít průvodce znovu
        </button>
      </div>

      {/* Uložit */}
      {error && <p style={{ color: theme.bad, fontSize: 13, marginBottom: 14 }}>{error}</p>}
      <button onClick={handleSave} disabled={loading} style={{ width: "100%", background: saved ? theme.ok : theme.blue, border: "none", borderRadius: theme.rBtn, padding: "13px", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 28, boxShadow: "0 4px 16px #126DFB30" }}>
        {loading ? "Ukládám…" : saved ? "✓ Uloženo" : "Uložit nastavení"}
      </button>

      {/* Nebezpečná zóna */}
      <div style={{ ...card, border: `1.5px solid ${theme.badLine}`, background: theme.badSoft }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: theme.bad, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14 }}>Nebezpečná zóna</p>
        {license?.stripe_subscription_id && (
          <button onClick={async () => {
            if (!window.confirm("Opravdu chcete zrušit předplatné? Bude aktivní do konce fakturačního období.")) return;
            try {
              await apiFetch("/cancel-subscription", token, { method: "POST" });
              alert("Předplatné bude zrušeno na konci fakturačního období.");
            } catch(e) { setError(e.message); }
          }} style={{ width: "100%", background: theme.bg, border: `1.5px solid ${theme.badLine}`, borderRadius: theme.rBtn, padding: "11px", color: theme.bad, fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 10, fontFamily: "inherit" }}>
            Zrušit předplatné
          </button>
        )}
        <button onClick={async () => { if (!window.confirm("Opravdu smazat všechny produkty?")) return; try { await apiFetch("/products/all", token, { method: "DELETE" }); window.location.reload(); } catch(e) { setError(e.message); } }} style={{ width: "100%", background: theme.bg, border: `1.5px solid ${theme.badLine}`, borderRadius: theme.rBtn, padding: "11px", color: theme.bad, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Smazat všechny produkty
        </button>
      </div>
    </>
  );
}
